import React, { useContext, useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import {
  ActionList,
  Badge,
  Button,
  Text,
  LegacyCard as Card,
  Layout,
  Link,
  Popover,
  ResourceItem,
  ResourceList,
  BlockStack,
  InlineStack,
  Thumbnail,
  Tooltip,
} from "@shopify/polaris";

import { ViewIcon } from "@shopify/polaris-icons";

import { baseHelper, imageHelper } from "lib/helpers";
import { GET_ORDER_LAKE_LINE_VIEW } from "app/orderLake/apollo/queries";
import { NotFound } from "layout/private/components";
import { Spinner } from "lib/components";
import constant from "lib/constant/constant";
import listData from "app/orderLake/modules/operator/features/list/orderListData";
import { PrivateContext } from "lib/context";
import { useNavigate } from "react-router-dom";

const LineItem = (props) => {
  const { cms, moneyFormat, orderId, vendorList, role } = props;
  const navigate = useNavigate();
  const { mainLink } = listData(cms);
  const [viewLineOrderData, setViewLineOrderData] = useState([]);
  const [isError, setIsError] = useState(false);
  const [totalOrderItem, setTotalOrderItem] = useState();
  const [totalOrderVendor, setTotalOrderVendor] = useState();
  const [popoverActive, setPopoverActive] = useState({});
  const [totalPrice, setTotalPrice] = useState(0);

  const { loading: loadingOrderLineView, data: dataOrderLineView } = useQuery(GET_ORDER_LAKE_LINE_VIEW, {
    variables: { input: { orderId } },
  });

  useEffect(() => {
    if (dataOrderLineView) {
      const orderLineView = baseHelper.getResponseData(dataOrderLineView, constant.gql.GET_ORDER_LAKE_LINE_VIEW);
      const orderLineViewError = baseHelper.getResponseError(dataOrderLineView, constant.gql.GET_ORDER_LAKE_LINE_VIEW);
      if (orderLineView) {
        setViewLineOrderData(orderLineView);
        setTotalOrderItem(orderLineView.length);
        const vendorsSum = [];
        orderLineView.filter(({ providerId }) => {
          vendorsSum.push(providerId);
          return vendorsSum;
        });
        const vendorCount = [...new Set(vendorsSum)];
        setTotalOrderVendor(vendorCount.length);
        const totalItemPrice =
          orderLineView &&
          orderLineView.map(({ price, basePrice, quantity }) => parseFloat(parseFloat(price || basePrice) * quantity));
        const totalAmount = totalItemPrice.reduce((cur, acc) => cur + acc, constant.value.ZERO);
        if (totalAmount) {
          setTotalPrice(totalAmount);
        }
      }
      if (orderLineViewError) {
        setIsError(true);
      }
    }
  }, [dataOrderLineView, setTotalOrderItem, setTotalOrderVendor]);

  if (loadingOrderLineView || !totalOrderItem || !totalOrderVendor) {
    return <Spinner size="small" />;
  }

  const renderItem = (item) => {
    const {
      _id: id,
      image,
      storeStatus,
      price,
      basePrice,
      quantity,
      name: productName,
      providerId,
      shippingStatus,
    } = item;

    const selectedVendor = vendorList.find((vendor) => vendor._id === providerId);
    const orderStatus =
      (shippingStatus === constant.DISPATCHED && constant.COMPLETE) ||
      (shippingStatus === constant.AWAITING_DISPATCH && constant.PROCESS) ||
      storeStatus;

    const size = constant.LARGE;
    const thumbnailImage = image
      ? imageHelper.resize({ url: image, type: constant.imageTypes.THUMBNAIL })
      : constant.NOIMAGESNAP;
    const thumbnail = <Thumbnail source={thumbnailImage} size={size} />;
    const togglePopoverActive = () => {
      setPopoverActive({ [id]: !popoverActive[id] });
    };
    const activator = (
      <Button onClick={togglePopoverActive} disclosure>
        Manage
      </Button>
    );

    const manageActions = (value) => {
      if (value === constant.VIEW_ID) {
        navigate(`${mainLink.orderNumber}${orderId}`);
      }
    };

    const option = [
      {
        content: constant.VIEW_LABEL,
        icon: ViewIcon,
        onAction: () => manageActions(constant.VIEW_ID),
      },
    ];

    const pageContent = {
      label: {
        status: cms("section.orderList.label.status"),
        fulfillmentStatus: cms("section.orderList.label.fulfillmentStatus"),
      },
    };

    return (
      <ResourceItem accessibilityLabel={`View details for ${id}`}>
        <Card.Section>
          <BlockStack>
            {thumbnail}
            <div className="card-description ">
              <Link removeUnderline onClick={() => navigate(`${mainLink.orderNumber}${orderId}`)}>
                {productName}
              </Link>
              <Text variant="bodySm" as="p">
                <Text as="span" tone="subdued">
                  {`${cms("section.orderList.label.vendor")}: `}
                  <Link removeUnderline url={`/providers/view/${providerId}`}>
                    {selectedVendor && selectedVendor.brandName}
                  </Link>
                </Text>
              </Text>
              <InlineStack>
                {orderStatus && (
                  <Tooltip content={pageContent.label.status} preferredPosition="above">
                    <Badge status={baseHelper.getBadgeType(orderStatus)}>{baseHelper.ucFirst(orderStatus)}</Badge>
                  </Tooltip>
                )}
              </InlineStack>
            </div>
            <Text as="span">{quantity}</Text>
            <Tooltip content={`${(parseFloat(price || basePrice) * quantity).toFixed(2)}`}>
              <span className="fix-content">
                {`${moneyFormat}${(parseFloat(price || basePrice) * quantity).toFixed(2)}`}
              </span>
            </Tooltip>
            <br />
            {/* <br /> */}
            <div className="manage-button">
              <Popover
                active={popoverActive[id]}
                activator={activator}
                onClose={() => setPopoverActive({ [id]: !popoverActive[id] })}
                setActive={() => setPopoverActive({ [id]: !popoverActive[id] })}
              >
                <ActionList items={option} />
              </Popover>
            </div>
          </BlockStack>
        </Card.Section>
      </ResourceItem>
    );
  };

  return (
    <>
      {isError && (
        <Layout.Section>
          <div style={{ display: "flex" }}>
            <NotFound />
          </div>
        </Layout.Section>
      )}
      <div className="card-header">
        <Card sectioned>
          <InlineStack>
            <Text as="h4">Product</Text>
            <Text as="h4">Description</Text>
            <Text as="h4">Quantity</Text>
            <Text as="h4">Price</Text>
          </InlineStack>
        </Card>
      </div>
      <ResourceList items={viewLineOrderData} renderItem={renderItem} />
      <div className="card-footer">
        <Card sectioned>
          <InlineStack>
            <Text as="span" fontWeight="semibold">
              Total
            </Text>
            <>
              <span className="light-text">
                {role === "vendor"
                  ? `(${totalOrderItem} ${totalOrderItem > 1 ? "Items" : "Item"})`
                  : `(${totalOrderItem} ${totalOrderItem > 1 ? "Items" : "Item"} from ${totalOrderVendor} ${
                      totalOrderVendor > 1 ? "Vendors" : "Vendor"
                    })`}
              </span>
              <Text as="span" fontWeight="semibold">{`${moneyFormat}${totalPrice.toFixed(2)}`}</Text>
            </>
          </InlineStack>
        </Card>
      </div>
    </>
  );
};

export default LineItem;
